// Shared empty-state card — the one way every Netwatch surface says "there's
// nothing here". Used inside the shared panels (SLA / Incidents / Outlook) and
// directly by the dashboard SPA (Backups, conversations drawer). Presentational
// only; hosts load the matching styles (`@netwatchai/chat-core/empty-state.css`;
// the widget inlines them into its shadow DOM).
//
// Four variants, because "empty" means four different things:
//   actionable — the user can fill this screen right now (CTA opens the flow)
//   info       — empty for a reason the user can't act on; explain it
//   positive   — empty IS the good outcome (zero incidents ≠ absence of data)
//   filter     — a search/filter hid everything; offer to clear it
//
// Illustrations are hand-drawn inline SVGs stroked with currentColor + the
// shared theme tokens, so they follow light/dark automatically and load no
// external assets (shadow-DOM / CSP safe).

import type { ReactNode } from 'react'

export type EmptyStateVariant = 'actionable' | 'info' | 'positive' | 'filter'

export type EmptyStateKind =
  | 'schedules'   // calendar + plus badge
  | 'chart'       // bar chart, outlined
  | 'search'      // magnifier
  | 'lock'        // padlock
  | 'shield'      // shield + check
  | 'disk'        // stacked device drives
  | 'chat'        // speech bubble
  | 'trend'       // history line + dotted projection

export interface EmptyStateAction {
  label: string
  onClick: () => void
}

export interface EmptyStateProps {
  kind: EmptyStateKind
  /** Defaults to 'info' — the safest reading of an empty screen. */
  variant?: EmptyStateVariant
  title?: string
  body?: ReactNode
  /** Primary CTA; omit when the viewer can't act (info/positive states). */
  action?: EmptyStateAction
  /** Smaller, borderless layout for in-panel / overlay spots. */
  compact?: boolean
}

// All scenes share one 96×76 canvas, 2px round strokes. `.ea` = main stroke
// (currentColor — tinted per variant by CSS), `.ea-dim` = supporting stroke,
// `.ea-fill` = soft currentColor wash, `.ea-acc` = accent detail.
const ART: Record<EmptyStateKind, ReactNode> = {
  schedules: (
    <>
      <rect className="ea ea-fill" x="20" y="16" width="52" height="48" rx="8" />
      <path className="ea" d="M20 30h52" />
      <path className="ea" d="M34 10v10M58 10v10" />
      <path className="ea-dim" d="M30 40h8M44 40h8M30 50h8" />
      <circle className="ea-acc ea-acc-fill" cx="70" cy="54" r="12" />
      <path className="ea-acc" d="M70 49v10M65 54h10" />
    </>
  ),
  chart: (
    <>
      <path className="ea-dim" d="M18 12v52h62" />
      <rect className="ea ea-fill" x="28" y="42" width="11" height="22" rx="2.5" />
      <rect className="ea ea-fill" x="46" y="30" width="11" height="34" rx="2.5" />
      <rect className="ea-dim" x="64" y="50" width="11" height="14" rx="2.5" strokeDasharray="4 3" />
    </>
  ),
  search: (
    <>
      <circle className="ea ea-fill" cx="42" cy="34" r="18" />
      <path className="ea" d="M55 47l14 14" />
      <path className="ea-dim" d="M34 30h16M34 38h10" />
    </>
  ),
  lock: (
    <>
      <path className="ea-dim" d="M34 36V26a14 14 0 0 1 28 0v10" />
      <rect className="ea ea-fill" x="28" y="36" width="40" height="30" rx="7" />
      <circle className="ea" cx="48" cy="49" r="3.5" />
      <path className="ea" d="M48 52v6" />
    </>
  ),
  shield: (
    <>
      <path className="ea ea-fill" d="M48 10l26 9v18c0 15-10.5 25-26 29-15.5-4-26-14-26-29V19z" />
      <path className="ea-acc" d="M37 38l8 8 15-16" />
    </>
  ),
  disk: (
    <>
      <rect className="ea ea-fill" x="24" y="14" width="48" height="15" rx="4" />
      <rect className="ea ea-fill" x="24" y="33" width="48" height="15" rx="4" />
      <rect className="ea-dim" x="24" y="52" width="48" height="15" rx="4" strokeDasharray="4 3" />
      <circle className="ea-acc ea-dot" cx="32" cy="21.5" r="1.8" />
      <circle className="ea-acc ea-dot" cx="32" cy="40.5" r="1.8" />
      <path className="ea-dim" d="M52 21.5h14M52 40.5h14" />
    </>
  ),
  chat: (
    <>
      <path className="ea ea-fill" d="M20 18h56a6 6 0 0 1 6 6v26a6 6 0 0 1-6 6H44L30 68V56H20a6 6 0 0 1-6-6V24a6 6 0 0 1 6-6z" />
      <circle className="ea-dot ea" cx="34" cy="37" r="1.6" />
      <circle className="ea-dot ea" cx="48" cy="37" r="1.6" />
      <circle className="ea-dot ea" cx="62" cy="37" r="1.6" />
    </>
  ),
  trend: (
    <>
      <path className="ea-dim" d="M18 12v52h62" />
      <path className="ea" d="M24 54l14-10 12 4 12-12" />
      <path className="ea-acc" d="M62 36l16-12" strokeDasharray="3 4" />
      <circle className="ea-acc ea-acc-fill" cx="78" cy="24" r="3" />
    </>
  ),
}

export function EmptyState({ kind, variant = 'info', title, body, action, compact }: EmptyStateProps) {
  return (
    <div className={`nw-es nw-es--${variant}${compact ? ' nw-es--compact' : ''}`}>
      <svg className="nw-es-art" viewBox="0 0 96 76" aria-hidden="true" focusable="false">
        {ART[kind]}
      </svg>
      {title && <div className="nw-es-t">{title}</div>}
      {body && <div className="nw-es-s">{body}</div>}
      {action && (
        <button type="button" className="nw-es-cta" onClick={action.onClick}>{action.label}</button>
      )}
    </div>
  )
}
